import { useEffect, useState } from 'react';
import axios from 'axios';
import { Database } from 'lucide-react';

/**
 * Detect env from the *frontend hostname* — far more reliable than parsing
 * the Mongo database name, which doesn't follow any guaranteed convention.
 */
function detectEnvFromHostname() {
  if (typeof window === 'undefined') return 'development';
  const h = window.location.hostname || '';
  if (h === 'resawork.fr' || h === 'www.resawork.fr') return 'production';
  if (h.includes('preview.emergentagent.com')) return 'preview';
  if (h === 'localhost' || h === '127.0.0.1') return 'development';
  return 'preview';  // any other domain → assume preview (safer than calling it prod)
}

/**
 * VersionBadge
 * Tiny footer badge showing the deployed version + DB name so admins can
 * tell at a glance which environment they're in (preview vs production).
 */
export default function VersionBadge() {
  const [info, setInfo] = useState(null);

  // Only ResaWork admins see the environment/version badge — for other roles
  // it's noise (they don't know what "PREVIEW" or a commit hash means).
  const isAdmin = (() => {
    try {
      const u = JSON.parse(localStorage.getItem('gardenbook_user') || 'null');
      return u?.role === 'admin';
    } catch { return false; }
  })();

  useEffect(() => {
    if (!isAdmin) return;
    const url = `${process.env.REACT_APP_BACKEND_URL || ''}/api/version`;
    axios.get(url)
      .then((res) => setInfo(res.data))
      .catch(() => {});
  }, [isAdmin]);

  if (!isAdmin || !info) return null;

  const buildDate = info.build_time
    ? new Date(info.build_time).toLocaleString('fr-FR', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : null;

  // Env is detected client-side from hostname (most reliable signal).
  const env = detectEnvFromHostname();

  const envBadge = {
    production: 'bg-red-100 text-red-700 border-red-200',
    preview: 'bg-amber-100 text-amber-700 border-amber-200',
    development: 'bg-slate-100 text-slate-600 border-slate-200',
  }[env];

  const envLabel = {
    production: 'PROD',
    preview: 'PREVIEW',
    development: 'DEV',
  }[env];

  return (
    <div
      className="text-[10px] leading-tight text-slate-400 px-3 py-2 select-none space-y-1"
      data-testid="version-badge"
      title={[
        info.commit ? `Commit ${info.commit}` : null,
        info.server_started_at ? `Serveur démarré : ${info.server_started_at}` : null,
        info.db_host ? `Mongo host : ${info.db_host}` : null,
        typeof window !== 'undefined' ? `Hostname : ${window.location.hostname}` : null,
      ].filter(Boolean).join('\n')}
    >
      <div className="flex items-center gap-1.5">
        <span className="font-mono font-medium text-slate-500">v{info.version}</span>
        <span
          className={`font-mono px-1.5 py-0.5 border rounded text-[9px] font-semibold tracking-wide ${envBadge}`}
          data-testid="env-badge"
        >
          {envLabel}
        </span>
        {info.commit && (
          <span className="font-mono text-slate-400">· {info.commit}</span>
        )}
      </div>
      {info.db_name && (
        <div
          className="flex items-center gap-1 text-slate-500 font-mono"
          data-testid="db-name-label"
        >
          <Database className="w-3 h-3 text-slate-400" />
          <span>{info.db_name}</span>
        </div>
      )}
      {buildDate && (
        <div className="text-slate-400">Build : {buildDate}</div>
      )}
    </div>
  );
}
